import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, effect, inject } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { Router, RouterModule } from '@angular/router';

import { KanbanBoardComponent } from '@components';
import { AiOrchestratorResult, ChatComponent, HistoryComponent } from '@components';
import { SplitPaneComponent, SplitPaneVerticalComponent } from '@components';
import { SkeletonBlockComponent } from '@components';
import { TicketsFacade, TicketsExecutionStore } from '@state';
import { DocsFacade } from '@state';
import {
  DEFAULT_AGENT_MODEL_ID,
  FeedbackService,
  ModalService,
  MACHINE_FEEDBACK_PROJECT_ID,
  ToastService,
  TicketsAutomationService,
  TicketsExecService,
  AGENT_MODEL_OPTIONS,
  type TicketsPreflightInput,
} from '@services';
import { applyJsonPatchOps, JsonPatchOp } from '../../utils/json-patch';
import { getDefinitionReadiness } from '../../utils/definition-readiness';
import { buildDefinitionCoverageChecklistItems } from '../../utils/definition-checklist.util';
import { buildTicketExecutionStartRuntime } from '../../utils/tickets-execution-start.util';
import { buildReposPayload, buildTicketsProjectContext, loadReposSettings } from '../../utils/tickets-repos-context.util';
import { compactDefinitionForOrchestrator } from '../../utils/orchestrator-definition-context.util';
import { normalizeColumnKind } from '../../state/tickets/tickets.models';
import { inferRepoRoleFromRepoRef, inferRepoRoleFromTicket, normalizeRepoRole, type RepoRole } from '../../utils/repo-role.util';

type TicketRisk = 'low' | 'med' | 'high';

type RepoCandidate = { owner: string; repo: string; role: RepoRole };

type RepoRoutingDecision = {
  installationId: number | null;
  target: RepoCandidate | null;
  choices: RepoCandidate[];
  confidence: number;
  reason: string;
  ambiguous: boolean;
  needsConfirmation: boolean;
};

@Component({
  selector: 'app-tickets',
  templateUrl: './tickets.page.html',
  styleUrls: ['./tickets.page.scss'],
  standalone: true,
  imports: [
    RouterModule,
    FormsModule,
    DecimalPipe,
    ChatComponent,
    SplitPaneComponent,
    SplitPaneVerticalComponent,
    HistoryComponent,
    KanbanBoardComponent,
    SkeletonBlockComponent
],
})
export class TicketsPage implements OnInit, OnDestroy {
  public boardReady = false;
  private loadedForProjectId: string | null = null;
  // DnD into Execute triggers both `cardDropped` and `boardCommit`.
  // We suppress the immediate commit save once so execute flow controls persistence.
  private suppressNextBoardCommitSave = false;
  private expectingGeneratedTickets = false;
  public ticketsSuggestions: any[] = [
    { icon: 'bi bi-bug', prompt: 'Create a new ticket to test my code' },
    {
      icon: 'bi bi-ticket-perforated',
      prompt: 'Complete all outstanding tickets',
    },
    {
      icon: 'bi bi-calendar-check',
      prompt: 'Generate a timesheet from all my tickets over the last month',
    },
    {
      icon: 'bi bi-ticket-perforated',
      prompt: 'Create some tickets for my Flutter app',
    },
  ];

  public ticketsContext: any = {
    page: 'tickets',
    purpose: 'Create, manage and review tickets and timelines',
    boards: ['Backlog', 'In Progress', 'Done'],
    priorities: ['Low', 'Medium', 'High', 'Critical'],
    outputs: ['New tickets', 'Backlog grooming', 'Timesheet generation']
  };

  public ticketsStore = inject(TicketsFacade);
  public execStore = inject(TicketsExecutionStore);
  private docs = inject(DocsFacade);
  private modal = inject(ModalService);
  private toast = inject(ToastService);
  private feedback = inject(FeedbackService);
  private execSvc = inject(TicketsExecService);
  private automation = inject(TicketsAutomationService);
  private router = inject(Router);

  public readonly machineFeedbackProjectId = MACHINE_FEEDBACK_PROJECT_ID;
  private readonly repoRoutingThreshold = 0.72;
  public agentifyingCardIds: string[] = [];
  public readonly repoRoleOptions: Array<{ id: RepoRole; label: string }> = [
    { id: 'site', label: 'Site (frontend)' },
    { id: 'api', label: 'API (backend)' },
    { id: 'agent-api', label: 'Agent API (orchestration)' },
    { id: 'infra', label: 'Infra/DevOps' },
    { id: 'unknown', label: 'Unknown / not sure' },
  ];
  public readonly riskOptions: Array<{ id: TicketRisk; label: string }> = [
    { id: 'low', label: 'Low' },
    { id: 'med', label: 'Medium' },
    { id: 'high', label: 'High' },
  ];
  public selectedTicket: {
    id: string;
    ticketNumber?: number;
    title: string;
    description?: string;
    repoRole?: RepoRole;
    acceptanceCriteria?: string[];
    risk?: TicketRisk;
  } | null = null;
  public execBusy = false;
  private ticketsExecutionModelId = DEFAULT_AGENT_MODEL_ID;
  public readonly execModelOptions = AGENT_MODEL_OPTIONS;
  public pendingExecution:
    | {
        ticket: {
          id: string;
          ticketNumber?: number;
          title: string;
          description?: string;
          repoRole?: RepoRole;
          acceptanceCriteria?: string[];
          risk?: TicketRisk;
        };
        fromColumnId: string;
        toColumnId: string;
        installationId: number;
        owner: string;
        repo: string;
        targetKey: string;
        repoChoices: RepoCandidate[];
        routingReason: string;
        routingConfidence: number;
        routingNeedsConfirmation: boolean;
        targetConfirmed: boolean;
        baseBranch?: string;
      }
    | null = null;
  // Use template ref `#ticketsChat` in the template.
  @ViewChild('ticketsChat') public ticketsChat?: ChatComponent;
  @ViewChild('executeModal') public executeModalTpl?: TemplateRef<any>;

  get definitionStatus() {
    const def = this.ticketsStore.projectDefinition();
    const loaded = this.boardReady;
    if (!loaded) {
      return { loaded: false, missingDefinition: false, ready: false, missing: [], counts: { goals: 0, features: 0, inScope: 0 } };
    }
    if (def === null) {
      return { loaded: true, missingDefinition: true, ready: false, missing: ['Project definition not found'], counts: { goals: 0, features: 0, inScope: 0 } };
    }
    const readiness = getDefinitionReadiness(def);
    return { loaded: true, missingDefinition: false, ...readiness };
  }

  get definitionReady(): boolean {
    return this.definitionStatus.loaded && !this.definitionStatus.missingDefinition && this.definitionStatus.ready;
  }

  get canGenerateTickets(): boolean {
    // Only allow generation when definition is ready and board is loaded.
    return this.definitionReady && this.boardReady;
  }

  get ticketsOrchestratorContext(): any {
    const board = this.ticketsStore.board() ?? [];
    const repoContext = this.ticketsStore.repoContext()?.github;
    return {
      definition: compactDefinitionForOrchestrator(this.ticketsStore.projectDefinition()),
      board: board.slice(0, 8).map((col: any) => ({
        id: col?.id,
        title: col?.title,
        cards: Array.isArray(col?.cards)
          ? col.cards.slice(0, 30).map((card: any) => ({
              id: card?.id,
              title: card?.title,
              repoRole: card?.repoRole,
              risk: card?.risk,
            }))
          : [],
      })),
      repoContext: repoContext
        ? {
            github: {
              installationId: repoContext.installationId ?? null,
              repos: Array.isArray(repoContext.repos) ? repoContext.repos.slice(0, 10) : [],
            },
          }
        : null,
    };
  }

  get generateChecklistItems(): Array<{ key: string; label: string; done: boolean; hint?: string }> {
    const status = this.definitionStatus;
    const goals = status.counts?.goals ?? 0;
    const features = status.counts?.features ?? 0;
    const inScope = status.counts?.inScope ?? 0;

    const missingSummary = Array.isArray(status.missing) && status.missing.length > 0 ? status.missing.join(' ') : '';

    return [
      {
        key: 'definition',
        label: 'Project definition exists',
        done: status.loaded && !status.missingDefinition,
        hint: status.loaded && status.missingDefinition ? 'Go to Define and create a project definition.' : undefined,
      },
      ...buildDefinitionCoverageChecklistItems(
        { goals, features, inScope },
        { hintSuffix: ' on Define.' },
      ),
      {
        key: 'board',
        label: 'Board loaded',
        done: this.boardReady,
        hint: this.boardReady ? undefined : 'Loading board…',
      },
      {
        key: 'ready',
        label: 'Ready to generate tickets',
        done: this.canGenerateTickets,
        hint: this.canGenerateTickets ? undefined : missingSummary || undefined,
      },
    ];
  }

  constructor() {
    effect(() => {
      const pid = this.ticketsStore.activeProjectId();
      if (!pid) return;
      if (this.loadedForProjectId === pid) return;
      this.automation.stopPolling();
      this.automation.currentRun.set(null);
      this.loadedForProjectId = pid;
      this.boardReady = false;
      // load() dispatches async doc + board loads; loading() goes true (loadStarted)
      // then false (boardLoaded). Wait for it to settle, then hydrate the persisted
      // run. Not an effect() — this runs inside a reactive context (NG0602).
      this.ticketsStore.load();
      this.afterTicketsLoaded();
    });
  }

  private afterTicketsLoaded(attempt = 0): void {
    if (!this.ticketsStore.loading()) {
      this.boardReady = true;
      void this.automation.hydratePersistedRun(this.execModelId);
      return;
    }
    if (attempt >= 80) {
      this.boardReady = true;
      return;
    }
    setTimeout(() => this.afterTicketsLoaded(attempt + 1), 75);
  }

  get resumableExecuteCardIds(): string[] {
    const run = this.automation.currentRun();
    if (!run || (run.status !== 'failed' && run.status !== 'partial')) return [];
    return (run.items ?? [])
      .filter((item: any) => String(item?.status ?? '').trim() !== 'review')
      .filter((item: any) => Array.isArray(item?.resumeDraft?.workUnits) && item.resumeDraft.workUnits.length > 0)
      .map((item: any) => String(item?.ticketId ?? '').trim())
      .filter(Boolean);
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.automation.stopPolling();
  }

  get execModelId(): string {
    const selected = String(this.ticketsStore.executionModelId() ?? '').trim();
    return selected || this.ticketsExecutionModelId || DEFAULT_AGENT_MODEL_ID;
  }

  set execModelId(value: string) {
    const next = String(value ?? '').trim() || DEFAULT_AGENT_MODEL_ID;
    this.ticketsExecutionModelId = next;
    this.ticketsStore.setExecutionModelId(next);
    this.ticketsChat?.setModelSelection(next);
  }

  get canAgentifyFeedbackTickets(): boolean {
    return String(this.ticketsStore.activeProjectId() ?? '').trim() === this.machineFeedbackProjectId;
  }

  goToDefine(): void {
    void this.router.navigate(['/define']);
  }

  generateTickets(): void {
    if (!this.canGenerateTickets) return;
    // The next tickets-page AI result is expected to carry board patches; arm a
    // guard so a patch-less reply surfaces as an error instead of silently doing
    // nothing (the orchestrator returns empty patches when its JSON won't parse).
    this.expectingGeneratedTickets = true;
    // Inject a prompt into chat (per chosen behavior).
    const prompt =
      'Generate the first implementation backlog from this project definition and current codebase. Create 12–25 actionable build-out tickets that prioritize enhancing existing pages/features (including Testing) over scaffolding. Do not add tickets for routes/pages/sidebar links that already exist. Cover core features, UX, testing automation, accessibility, performance, CI/deployment, and docs. Put them in Backlog.';
    // `ticketsChat` is bound via template ref in the HTML.
    this.ticketsChat?.sendInjectedPrompt(prompt);
  }

  async onChatMessagesChange(messages: Array<{ role: 'user' | 'assistant'; text: string }>) {
    await this.ticketsStore.setChatMessages(messages);
  }

  async onChatMessageCreated(msg: { role: 'user' | 'assistant'; text: string }) {
    await this.ticketsStore.logChatEvent(msg.text, msg.role);
  }

  async onAiResult(result: AiOrchestratorResult) {
    if (!result || String(result.page ?? '').toLowerCase() !== 'tickets') return;
    const ops = (Array.isArray(result.patches) ? result.patches : []) as JsonPatchOp[];
    if (ops.length === 0) {
      // A patch-less reply is normal for conversational messages, but if the
      // user explicitly asked to generate tickets it means the model failed to
      // produce a usable board update — don't let that fail silently.
      if (this.expectingGeneratedTickets) {
        this.expectingGeneratedTickets = false;
        this.toast.error(
          'The assistant did not return any ticket changes. Please try again or rephrase the request.',
          'Tickets'
        );
      }
      return;
    }
    this.expectingGeneratedTickets = false;

    try {
      const currentBoard = this.ticketsStore.board();
      const wrappedNext = applyJsonPatchOps<{ board: any }>(
        { board: currentBoard },
        ops.map((op) => {
          const path = String((op as any)?.path ?? '');
          // Normalize if agent ever emits /columns... instead of /board/columns...
          if (path === '/columns' || path.startsWith('/columns/')) {
            return { ...(op as any), path: `/board${path}` } as JsonPatchOp;
          }
          return op;
        })
      );

      const nextBoard = Array.isArray(wrappedNext?.board) ? wrappedNext.board : [];
      await this.ticketsStore.setBoardDraft(nextBoard);
      await this.ticketsStore.saveBoard();
      this.toast.success('AI updated ticket board', 'Tickets');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to apply AI ticket updates', 'Tickets');
    }
  }

  onCardSelected(ev: { columnId: string; card: { id: string; title: string; description?: string } }) {
    const card = ev?.card;
    if (!card) return;
    this.selectedTicket = {
      id: String(card.id ?? '').trim(),
      ticketNumber: typeof (card as any)?.ticketNumber === 'number' ? (card as any).ticketNumber : Number((card as any)?.ticketNumber ?? 0) || undefined,
      title: String(card.title ?? '').trim(),
      description: String(card.description ?? '').trim() || undefined,
      repoRole: normalizeRepoRole((card as any)?.repoRole),
      acceptanceCriteria: this.normalizeAcceptanceCriteria((card as any)?.acceptanceCriteria),
      risk: this.normalizeRisk((card as any)?.risk),
    };
  }

  private isExecuteColumnId(columnId: string): boolean {
    const col = (this.ticketsStore.board() ?? []).find((c: any) => String(c?.id ?? '') === String(columnId ?? ''));
    return normalizeColumnKind((col as any)?.kind, (col as any)?.title) === 'execute';
  }

  private findColumnIdByTitle(title: string): string | null {
    const t = String(title ?? '').trim().toLowerCase();
    const col = (this.ticketsStore.board() ?? []).find((c: any) => String(c?.title ?? '').trim().toLowerCase() === t);
    return col ? String(col.id ?? '') : null;
  }

  private findColumnIdByKind(kind: 'backlog' | 'execute' | 'review' | 'done'): string | null {
    const col = (this.ticketsStore.board() ?? []).find((entry: any) => normalizeColumnKind(entry?.kind, entry?.title) === kind);
    return col ? String(col.id ?? '') : null;
  }

  onCardDropped(ev: { card: { id: string; title: string; description?: string }; fromColumnId: string; toColumnId: string }) {
    if (!ev || !ev.card) return;
    if (!this.isExecuteColumnId(ev.toColumnId)) return;
    this.suppressNextBoardCommitSave = true;
    void this.prepareAndOpenExecuteModal(ev);
  }

  async onAgentifyCard(ev: { columnId: string; card: { id: string; title: string; ticketNumber?: number } }) {
    const projectId = String(this.ticketsStore.activeProjectId() ?? '').trim();
    const ticketId = String(ev?.card?.id ?? '').trim();
    if (!projectId) {
      this.toast.warning('Select a project first', 'Tickets');
      return;
    }
    if (!ticketId) {
      this.toast.warning('Ticket id is required', 'Tickets');
      return;
    }
    if (this.agentifyingCardIds.includes(ticketId)) return;

    this.agentifyingCardIds = [...this.agentifyingCardIds, ticketId];
    try {
      const reposSettings = await loadReposSettings(this.docs, projectId);
      const result = await this.feedback.agentifyTicket({
        projectId,
        ticketId,
        ticketNumber:
          typeof ev?.card?.ticketNumber === 'number'
            ? ev.card.ticketNumber
            : Number(String((ev?.card as any)?.ticketNumber ?? '').trim()) || undefined,
        title: String(ev?.card?.title ?? '').trim() || undefined,
        source: 'manual',
        projectContext: buildTicketsProjectContext(this.ticketsStore, reposSettings ?? undefined),
      });
      await this.ticketsStore.refreshBoard(projectId);
      if (result.agentify.status === 'completed' && result.agentify.movedToBacklog) {
        const ticketLabel = result.card.ticketNumber ? `Ticket #${result.card.ticketNumber}` : 'Ticket';
        this.toast.success(`${ticketLabel} moved to ${result.column.title}.`, 'Tickets');
      } else {
        this.toast.error(result.agentify.error ?? 'Agentify failed', 'Tickets');
      }
    } catch (e: any) {
      await this.ticketsStore.refreshBoard(projectId);
      this.toast.error(e?.message ?? 'Failed to agentify ticket', 'Tickets');
    } finally {
      this.agentifyingCardIds = this.agentifyingCardIds.filter((id) => id !== ticketId);
    }
  }

  async onResumeCard(ev: { columnId: string; card: { id: string; title: string } }) {
    const run = this.automation.currentRun();
    const runId = String(run?.id ?? '').trim();
    const ticketId = String(ev?.card?.id ?? '').trim();
    if (!runId || !ticketId) {
      this.toast.warning('No failed run is available to resume.', 'Tickets');
      return;
    }
    if (!this.resumableExecuteCardIds.includes(ticketId)) {
      this.toast.warning('This ticket does not have a resumable failed draft.', 'Tickets');
      return;
    }
    const resumed = await this.automation.resumeRun(runId, this.execModelId, ticketId);
    if (resumed) {
      this.toast.success(`Retry started for ${String(ev?.card?.title ?? 'ticket').trim() || 'ticket'}.`, 'Tickets');
    }
  }

  private async prepareAndOpenExecuteModal(ev: { card: { id: string; title: string; description?: string }; fromColumnId: string; toColumnId: string }) {
    const ticket = this.normalizeTicketForExecution({
      id: String(ev.card.id ?? '').trim(),
      title: String(ev.card.title ?? '').trim(),
      description: String(ev.card.description ?? '').trim() || undefined,
      repoRole: (ev.card as any)?.repoRole,
      acceptanceCriteria: (ev.card as any)?.acceptanceCriteria,
      risk: (ev.card as any)?.risk,
    });
    if (!ticket.id || !ticket.title) {
      await this.revertCardMove(String(ev.card.id ?? ''), ev.fromColumnId, ev.toColumnId);
      this.toast.warning('Ticket id and title are required', 'Tickets');
      return;
    }

    const projectId = this.ticketsStore.activeProjectId();
    if (!projectId) {
      await this.revertCardMove(ticket.id, ev.fromColumnId, ev.toColumnId);
      this.toast.warning('Select a project first', 'Tickets');
      return;
    }

    try {
      const reposSettings = await loadReposSettings(this.docs, projectId);
      const plan = await this.execSvc.planTicketExecution({
        projectId,
        installationId: Number(reposSettings?.github?.installationId ?? 0) || undefined,
        repos: buildReposPayload(reposSettings),
        ticket,
        projectContext: buildTicketsProjectContext(this.ticketsStore, reposSettings),
      });
      if (!plan.installationId || plan.choices.length === 0) {
        await this.revertCardMove(ticket.id, ev.fromColumnId, ev.toColumnId);
        this.toast.warning('Connect GitHub and select a repo on the Repos page first', 'Tickets');
        return;
      }

      const target = plan.target ?? null;
      this.pendingExecution = {
        ticket: this.normalizeTicketForExecution(plan.ticket),
        fromColumnId: ev.fromColumnId,
        toColumnId: ev.toColumnId,
        installationId: plan.installationId,
        owner: target?.owner ?? '',
        repo: target?.repo ?? '',
        targetKey: target ? `${target.owner}/${target.repo}` : '',
        repoChoices: plan.choices,
        routingReason: plan.reason,
        routingConfidence: plan.confidence,
        routingNeedsConfirmation: plan.needsConfirmation,
        targetConfirmed: !plan.needsConfirmation,
        baseBranch: 'dev',
      };

      if (plan.canAutoExecute && target) {
        try {
          await this.persistTicketMetadata(this.pendingExecution.ticket);
        } catch {}
        const pending = this.pendingExecution;
        const model = String(this.execModelId ?? '').trim();
        this.execStore.setExecutionRuntime(
          buildTicketExecutionStartRuntime(pending, model),
        );
        this.pendingExecution = null;
        void this.executeTicketInBackground(pending, model);
        return;
      }

      if (!this.executeModalTpl) {
        this.toast.error('Execute modal not available', 'Tickets');
        return;
      }

      this.modal.open(this.executeModalTpl, { centered: true, backdrop: 'static', keyboard: true });
    } catch (e: any) {
      await this.revertCardMove(ticket.id, ev.fromColumnId, ev.toColumnId);
      this.toast.error(e?.message ?? 'Failed to prepare ticket execution', 'Tickets');
    }
  }

  async onExecuteModalCancel(_modal: any) {
    const pending = this.pendingExecution;
    if (!pending) return;
    await this.revertCardMove(pending.ticket.id, pending.fromColumnId, pending.toColumnId);
    this.pendingExecution = null;
    this.modal.dismiss();
  }

  async onExecuteModalConfirm(_modal: any) {
    const pending = this.pendingExecution;
    if (!pending) return;
    if (this.execBusy) return;
    const validationError = this.validatePendingExecution(pending);
    if (validationError) {
      this.toast.warning(validationError, 'Tickets');
      return;
    }
    try {
      await this.persistTicketMetadata(pending.ticket);
    } catch {}
    this.execBusy = true;
    const model = String(this.execModelId ?? '').trim();
    this.execStore.setExecutionRuntime(buildTicketExecutionStartRuntime(pending, model));

    // Non-blocking UX: close modal immediately, continue execution in background.
    this.modal.close();
    this.pendingExecution = null;
    this.execBusy = false;

    void this.executeTicketInBackground(pending, model);
  }

  private async executeTicketInBackground(
    pending: NonNullable<TicketsPage['pendingExecution']>,
    model: string
  ): Promise<void> {
    try {
      this.execStore.setExecutionRuntime({ phase: 'planning' });

      const projectId = this.ticketsStore.activeProjectId();
      const reposSettings = projectId ? await loadReposSettings(this.docs, projectId) : null;
      const repos = buildReposPayload(reposSettings);
      await this.runPreflightOrThrow({
        installationId: pending.installationId,
        repos,
        target: {
          owner: pending.owner,
          repo: pending.repo,
          role: (pending as any)?.ticket?.repoRole ?? undefined,
          confidence: pending.routingConfidence,
          reason: pending.routingReason,
          source: pending.targetConfirmed ? 'user-confirmed' : 'auto',
        },
        baseBranch: 'dev',
      });

      const run = await this.automation.startTicketExecutionRun({
        projectId: projectId ?? '',
        installationId: pending.installationId,
        repos,
        target: {
          owner: pending.owner,
          repo: pending.repo,
          role: (pending as any)?.ticket?.repoRole ?? undefined,
          confidence: pending.routingConfidence,
          reason: pending.routingReason,
          source: pending.targetConfirmed ? 'user-confirmed' : 'auto',
        },
        model: model || undefined,
        ticket: pending.ticket as any,
        projectContext: buildTicketsProjectContext(this.ticketsStore, reposSettings),
      });
      if (!run) throw new Error('Failed to start background ticket execution');
    } catch (e: any) {
      await this.revertCardMove(pending.ticket.id, pending.fromColumnId, pending.toColumnId);
      const msg = e?.message ?? 'Failed to execute ticket';
      this.execStore.setExecutionRuntime({
        active: false,
        phase: 'failed',
        error: msg,
      });
      this.toast.error(msg, 'Tickets');
    }
  }

  private async runPreflightOrThrow(input: TicketsPreflightInput): Promise<void> {
    const pre = await this.execSvc.preflight({
      installationId: input.installationId,
      repos: input.repos,
      target: input.target,
      baseBranch: input.baseBranch ?? 'dev',
    });
    if (pre?.ok) return;
    const checks = Array.isArray(pre?.checks) ? pre.checks : [];
    const failed = checks.filter((c: any) => !c?.ok).map((c: any) => String(c?.message ?? '').trim()).filter(Boolean);
    const msg = failed.length > 0 ? failed.join(' | ') : 'Preflight failed';
    throw new Error(msg);
  }

  private applyCardUpdateAndMove(input: { cardId: string; appendDescription?: string; fromColumnId: string; toColumnId: string }) {
    const board: any[] = this.ticketsStore.board() ?? [];
    const cardId = String(input.cardId ?? '');
    const fromId = String(input.fromColumnId ?? '');
    const toId = String(input.toColumnId ?? '');

    let movingCard: any = null;

    const without = board.map((col: any) => {
      const cards = (col.cards ?? []) as any[];
      if (String(col.id ?? '') === fromId) {
        const idx = cards.findIndex((c) => String(c?.id ?? '') === cardId);
        if (idx >= 0) {
          const copy = [...cards];
          movingCard = copy.splice(idx, 1)[0];
          return { ...col, cards: copy };
        }
      }
      return col;
    });

    if (!movingCard) {
      for (const col of without) {
        const cards = (col.cards ?? []) as any[];
        const idx = cards.findIndex((c) => String(c?.id ?? '') === cardId);
        if (idx >= 0) {
          movingCard = cards[idx];
          col.cards = cards.filter((c) => String(c?.id ?? '') !== cardId);
          break;
        }
      }
    }

    if (!movingCard) return without;

    if (input.appendDescription) {
      const existing = String(movingCard?.description ?? '').trim();
      movingCard = {
        ...movingCard,
        description: existing ? `${existing}\n\n${input.appendDescription}` : input.appendDescription,
      };
    }

    return without.map((col: any) => {
      if (String(col.id ?? '') !== toId) return col;
      const nextCards = [...(col.cards ?? []), movingCard];
      return { ...col, cards: nextCards };
    });
  }

  private async revertCardMove(cardId: string, fromColumnId: string, toColumnId: string) {
    const next = this.applyCardUpdateAndMove({
      cardId: String(cardId ?? ''),
      fromColumnId: String(toColumnId ?? ''), // currently here
      toColumnId: String(fromColumnId ?? ''),
    });
    await this.ticketsStore.setBoardDraft(next as any);
    await this.ticketsStore.saveBoard();
  }

  private normalizeRisk(raw: any): TicketRisk | undefined {
    const v = String(raw ?? '').trim().toLowerCase();
    if (v === 'low' || v === 'med' || v === 'high') return v;
    return undefined;
  }

  private normalizeAcceptanceCriteria(raw: any): string[] {
    if (!Array.isArray(raw)) return [];
    return raw
      .map((x: any) => String(x ?? '').trim())
      .filter(Boolean)
      .slice(0, 8);
  }

  private normalizeTicketForExecution(ticket: any): NonNullable<TicketsPage['pendingExecution']>['ticket'] {
    const description = String(ticket?.description ?? '').trim() || undefined;
    const inferredRole = inferRepoRoleFromTicket(ticket);
    const repoRole = normalizeRepoRole(ticket?.repoRole) ?? (inferredRole !== 'unknown' ? inferredRole : 'unknown');
    const acceptanceCriteria = this.normalizeAcceptanceCriteria(ticket?.acceptanceCriteria);
    const risk =
      this.normalizeRisk(ticket?.risk) ??
      (repoRole === 'api' || repoRole === 'agent-api'
        ? 'med'
        : /(auth|security|database|migration|schema|payment|token)/.test(`${String(ticket?.title ?? '')} ${String(description ?? '')}`.toLowerCase())
        ? 'high'
        : 'low');
    const normalized = {
      id: String(ticket?.id ?? '').trim(),
      ticketNumber:
        (typeof ticket?.ticketNumber === 'number' ? ticket.ticketNumber : Number(String(ticket?.ticketNumber ?? '').trim())) > 0
          ? Math.floor(typeof ticket?.ticketNumber === 'number' ? ticket.ticketNumber : Number(String(ticket?.ticketNumber ?? '').trim()))
          : undefined,
      title: String(ticket?.title ?? '').trim(),
      description,
      repoRole,
      acceptanceCriteria:
        acceptanceCriteria.length > 0
          ? acceptanceCriteria
          : [
              `${String(ticket?.title ?? '').trim() || 'Ticket'} is implemented successfully.`,
              'The change is scoped to the intended repository.',
              'Validation or tests cover the change.',
            ],
      risk,
    };
    return normalized;
  }

  private resolveRepoTarget(settings: any, ticket: any): RepoRoutingDecision {
    const installationIdRaw = settings?.github?.installationId ?? null;
    const installationId = typeof installationIdRaw === 'number' ? installationIdRaw : Number(String(installationIdRaw ?? '').trim());
    const validInstallationId = Number.isFinite(installationId) && installationId > 0 ? installationId : null;
    const reposRaw = Array.isArray(settings?.github?.repos) ? settings.github.repos : [];
    const repos: RepoCandidate[] = reposRaw
      .map((r: any) => ({
        owner: String(r?.owner ?? '').trim(),
        repo: String(r?.name ?? '').trim(),
        role: inferRepoRoleFromRepoRef(r),
      }))
      .filter((r: RepoCandidate) => !!r.owner && !!r.repo);

    const explicitRole = normalizeRepoRole(ticket?.repoRole);
    const inferredRole = inferRepoRoleFromTicket(ticket);
    const effectiveRole: RepoRole | null =
      explicitRole && explicitRole !== 'unknown'
        ? explicitRole
        : inferredRole && inferredRole !== 'unknown'
        ? inferredRole
        : null;
    const roleMatches = effectiveRole ? repos.filter((r) => r.role === effectiveRole) : [];

    let target: RepoCandidate | null = null;
    let confidence = 0;
    let reason = 'No routing candidate available.';
    let ambiguous = false;

    if (roleMatches.length === 1) {
      target = roleMatches[0] ?? null;
      confidence = explicitRole ? 0.95 : 0.84;
      reason = `Matched ticket role '${effectiveRole}' to a single repo.`;
    } else if (roleMatches.length > 1) {
      ambiguous = true;
      target = roleMatches[0] ?? null;
      confidence = 0.58;
      reason = `Multiple repos match role '${effectiveRole}'. Manual confirmation required.`;
    } else if (repos.length === 1) {
      target = repos[0] ?? null;
      confidence = 0.82;
      reason = 'Only one connected repo is available.';
    } else if (repos.length > 1) {
      ambiguous = true;
      target = repos[0] ?? null;
      confidence = 0.4;
      reason = 'No clear role match across multiple repos.';
    }

    const needsConfirmation = !target || ambiguous || confidence < this.repoRoutingThreshold;
    return {
      installationId: validInstallationId,
      target,
      choices: repos,
      confidence,
      reason,
      ambiguous,
      needsConfirmation,
    };
  }

  onPendingTargetRepoChanged(targetKey: string) {
    if (!this.pendingExecution) return;
    const key = String(targetKey ?? '').trim();
    const match = this.pendingExecution.repoChoices.find((r) => `${r.owner}/${r.repo}` === key) ?? null;
    if (!match) return;
    this.pendingExecution.owner = match.owner;
    this.pendingExecution.repo = match.repo;
    this.pendingExecution.targetKey = key;
    this.pendingExecution.targetConfirmed = true;
  }

  onPendingAcceptanceCriteriaChange(raw: string) {
    if (!this.pendingExecution) return;
    const lines = String(raw ?? '')
      .split('\n')
      .map((x) => x.replace(/^[-*]\s*/, '').trim())
      .filter(Boolean);
    this.pendingExecution.ticket.acceptanceCriteria = lines.slice(0, 8);
  }

  private syncPendingTargetConfirmation(pending: NonNullable<TicketsPage['pendingExecution']>): boolean {
    const targetKey = String(pending.targetKey ?? '').trim();
    const resolvedKey = `${String(pending.owner ?? '').trim()}/${String(pending.repo ?? '').trim()}`;
    const hasResolvedTarget = !!pending.owner && !!pending.repo;
    const hasSelectedTarget = !!targetKey && targetKey === resolvedKey;
    if (hasResolvedTarget && hasSelectedTarget) {
      pending.targetConfirmed = true;
      return true;
    }
    return !!pending.targetConfirmed;
  }

  private validatePendingExecution(pending: NonNullable<TicketsPage['pendingExecution']>): string | null {
    if (!pending.owner || !pending.repo) return 'Pick a target repo before executing.';
    if (pending.routingNeedsConfirmation && !this.syncPendingTargetConfirmation(pending)) {
      return 'Confirm the target repo before executing.';
    }
    const normalized = this.normalizeTicketForExecution(pending.ticket);
    pending.ticket = normalized;
    if (!normalized.repoRole || normalized.repoRole === 'unknown') {
      return 'Select the ticket scope (repo role) before executing.';
    }
    if (!normalized.risk) return 'Set ticket risk before executing.';
    if (!normalized.acceptanceCriteria || normalized.acceptanceCriteria.length === 0) {
      return 'Add at least one acceptance criterion before executing.';
    }
    return null;
  }

  private async persistTicketMetadata(ticket: NonNullable<TicketsPage['pendingExecution']>['ticket']): Promise<void> {
    const next = (this.ticketsStore.board() ?? []).map((col: any) => ({
      ...col,
      cards: (col.cards ?? []).map((c: any) => {
        if (String(c?.id ?? '') !== String(ticket.id ?? '')) return c;
        return {
          ...c,
          repoRole: ticket.repoRole,
          risk: ticket.risk,
          acceptanceCriteria: ticket.acceptanceCriteria,
        };
      }),
    }));
    await this.ticketsStore.setBoardDraft(next as any);
    await this.ticketsStore.saveBoard();
  }

  async executeSelectedTicket() {
    if (!this.selectedTicket) return;
    const ticket = this.normalizeTicketForExecution(this.selectedTicket);
    const ticketId = String(ticket.id ?? '').trim();
    const title = String(ticket.title ?? '').trim();
    const description = String(ticket.description ?? '').trim() || undefined;
    if (!ticketId || !title) {
      this.toast.warning('Ticket id and title are required', 'Tickets');
      return;
    }
    if (!ticket.repoRole || ticket.repoRole === 'unknown') {
      this.toast.warning('Select a repo role for this ticket before executing.', 'Tickets');
      return;
    }
    if (!ticket.risk) {
      this.toast.warning('Set ticket risk before executing.', 'Tickets');
      return;
    }
    if (!ticket.acceptanceCriteria || ticket.acceptanceCriteria.length === 0) {
      this.toast.warning('Add acceptance criteria before executing.', 'Tickets');
      return;
    }

    const projectId = this.ticketsStore.activeProjectId();
    if (!projectId) {
      this.toast.warning('Select a project first', 'Tickets');
      return;
    }

    this.execBusy = true;
    try {
      const reposSettings = await loadReposSettings(this.docs, projectId);
      const routing = this.resolveRepoTarget(reposSettings, ticket);
      if (!routing.installationId || !routing.target) {
        this.toast.warning('Connect GitHub and select a repo on the Repos page first', 'Tickets');
        return;
      }
      if (routing.needsConfirmation) {
        this.toast.warning('Routing is ambiguous. Use drag-to-Execute flow and confirm target repo in the modal.', 'Tickets');
        return;
      }
      const target = routing.target;
      const repos = buildReposPayload(reposSettings);
      await this.runPreflightOrThrow({
        installationId: routing.installationId,
        repos,
        target: {
          owner: target.owner,
          repo: target.repo,
          role: ticket.repoRole,
          confidence: routing.confidence,
          reason: routing.reason,
          source: 'auto',
        },
        baseBranch: 'dev',
      });

      const res = await this.execSvc.executeTicket({
        installationId: routing.installationId,
        owner: target.owner,
        repo: target.repo,
        baseBranch: 'dev',
        ticket: {
          id: ticketId,
          title,
          description,
          repoRole: ticket.repoRole,
          acceptanceCriteria: ticket.acceptanceCriteria,
          risk: ticket.risk,
        },
      });

      // Update the ticket card with PR link.
      const prUrl = String(res?.pullRequest?.url ?? '').trim();
      const prNumber = Number(res?.pullRequest?.number ?? 0);
      const prLine = prUrl ? `PR: ${prUrl}` : prNumber ? `PR: #${prNumber}` : 'PR created';

      const next = (this.ticketsStore.board() ?? []).map((col: any) => ({
        ...col,
        cards: (col.cards ?? []).map((c: any) => {
          if (String(c?.id ?? '') !== ticketId) return c;
          const existing = String(c?.description ?? '').trim();
          const updated = existing ? `${existing}\n\n${prLine}` : prLine;
          return { ...c, description: updated };
        }),
      }));

      await this.ticketsStore.setBoardDraft(next as any);
      await this.ticketsStore.saveBoard();

      this.toast.success('Ticket executed (PR created)', 'Tickets');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to execute ticket', 'Tickets');
    } finally {
      this.execBusy = false;
    }
  }

  async onBoardChange(columns: any) {
    await this.ticketsStore.setBoardDraft(columns);
  }

  async onBoardCommit(columns: any) {
    await this.ticketsStore.setBoardDraft(columns);
    if (this.suppressNextBoardCommitSave) {
      this.suppressNextBoardCommitSave = false;
      return;
    }
    await this.ticketsStore.saveBoard();
  }
}
